// AssessmentProgression.js
import * as React from "react";
import { Card, CardContent, Typography, Box, useTheme, Divider, Stack } from "@mui/material";
import {
  ResponsiveContainer,
  FunnelChart,
  Funnel,
  Tooltip,
  LabelList,
  Cell,
} from "recharts";

/**
 * Props:
 *  data: {
 *    enrolled_count: number,
 *    assessed_count: number,
 *    intrem_assessed_count: number, // (typo preserved from payload)
 *    post_assessed_count: number
 *  }
 */



export default function AssessmentProgression({ data = {} }) {
  const theme = useTheme();

  if (!data) { 
    return (
        <Box sx={{ p: 1 }}>
            <Box sx={{ mb: 1 }}>
            <Stack spacing={1} alignItems="center">
            <Typography variant="h6" fontWeight="bold">
                Assessment Progression
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Enrolled → Assessed → Interim → Post
                </Typography>
            </Stack>
            </Box>
            <Divider />
            <Box>
                <Typography variant="body2">Requires semester filter</Typography>
            </Box>
        </Box>
    )
  }
  const enrolled = Number(data.enrolled_count ?? 0);
  // Ensure funnel is non-increasing so the shape renders nicely
  const assessed = Math.min(Number(data.assessed_count ?? 0), enrolled);
  const interim = Math.min(Number(data.intrem_assessed_count ?? 0), assessed);
  const post = Math.min(Number(data.post_assessed_count ?? 0), interim);

  const rows = [
    { name: "Enrolled", value: enrolled },
    { name: "Assessed", value: assessed },
    { name: "Interim", value: interim },
    { name: "Post", value: post },
  ];

  const total = enrolled || 1; // avoid divide-by-zero
  const percentFmt = (v) => `${((v / total) * 100).toFixed(0)}%`;

  const fills = [
    theme.palette.primary.main,
    theme.palette.info.main,
    theme.palette.warning.main,
    theme.palette.success.main,
  ];

  const hasData = rows.some((r) => r.value > 0);

  return (

    <Box sx={{ p: 1 }}>
        <Box sx={{ mb: 1 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Box>
            <Typography variant="subtitle1" fontWeight="bold">
              Student progression 
            </Typography>
            <Typography variant="body2" color="text.secondary">
              &nbsp;Student assessment progression funnel
            </Typography>
          </Box>
        </Stack>
        <Divider sx={{ mt: 1 }} />
      </Box>
        <Box sx={{ height: 300 }}>
          {hasData ? (
            <ResponsiveContainer style={{paddingTop: 5}} width="100%" height="100%">
              <FunnelChart margin={{ top: 10, right: 0, bottom: 0, left: 0 }}>
                <Tooltip
                  formatter={(value, name, props) => [
                    `${value} (${percentFmt(value)})`,
                    name,
                  ]}
                />
                <Funnel
                  dataKey="value"
                  data={rows}
                  isAnimationActive
                  width={'70%'}
                  fill="#000"
                >
                  {/* Colored segments */}
                  {rows?.map((entry, idx) => (
                    <Cell key={entry.name} fill={fills[idx % fills.length]} />
                  ))}

                  {/* Left label: stage name */}
                  <LabelList
                    position="left"
                    dataKey="name"
                    stroke="none"
                    fill={theme.palette.text.primary}
                    style={{ fontWeight: 600 }}
                  />
                  {/* Right label: count + % of enrolled */}
                  <LabelList
                    position="right"
                    dataKey="value"
                    formatter={(v) => `${v} • ${percentFmt(v)}`}
                    stroke="none"
                    fill={theme.palette.text.secondary}
                  />
                </Funnel>
              </FunnelChart>
            </ResponsiveContainer>
          ) : (
            <Box
              sx={{
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "text.secondary",
              }}
            >
              <Typography variant="body2">No data to display.</Typography>
            </Box>
          )}
        </Box>

        {/* Tiny legend */}
        <Box sx={{ mt: 2, display: "flex", gap: 2, flexWrap: "wrap" }}>
          {rows.map((r, i) => (
            <Box key={r.name} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box sx={{ width: 12, height: 12, bgcolor: fills[i % fills.length], borderRadius: 0.5 }} />
              <Typography variant="caption" color="text.secondary">
                {r.name}
              </Typography>
            </Box>
          ))}
        </Box>

        
    </Box>
  );
}